import { ref, computed, reactive } from 'vue';

/**
 * Composable for managing linked operator selection mode.
 * This enables users to select parent operators directly on the flow canvas
 * instead of selecting from a list in the form.
 */

export interface LinkedOperatorSelectionState {
  /** Whether selection mode is active */
  isActive: boolean;
  /** Types of operators that can be selected */
  selectableTypes: string[];
  /** IDs of ancestor steps that can be selected (only these are valid) */
  selectableStepIds: string[];
  /** Currently selected operator step IDs */
  selectedStepIds: string[];
  /** Callback to invoke when selection is confirmed */
  onConfirm: ((stepIds: string[]) => void) | null;
  /** Callback to invoke when selection is cancelled */
  onCancel: (() => void) | null;
  /** Callback to reopen the panel after selection */
  onReopenPanel: (() => void) | null;
  /** Last confirmed selection (to be retrieved by the form) */
  lastConfirmedSelection: string[] | null;
}

const state = reactive<LinkedOperatorSelectionState>({
  isActive: false,
  selectableTypes: [],
  selectableStepIds: [],
  selectedStepIds: [],
  onConfirm: null,
  onCancel: null,
  onReopenPanel: null,
  lastConfirmedSelection: null,
});

/**
 * Start linked operator selection mode
 * @param selectableTypes - Array of operator types that can be selected
 * @param selectableStepIds - Array of step IDs that can be selected (ancestors only)
 * @param initialSelection - Array of already selected step IDs
 * @param onConfirm - Callback when user confirms selection
 * @param onCancel - Callback when user cancels selection
 * @param onReopenPanel - Callback to reopen the panel after selection
 */
export function startLinkedOperatorSelection(
  selectableTypes: string[],
  selectableStepIds: string[],
  initialSelection: string[],
  onConfirm: (stepIds: string[]) => void,
  onCancel: () => void,
  onReopenPanel?: () => void
) {
  state.selectableTypes = [...selectableTypes];
  state.selectableStepIds = [...selectableStepIds];
  state.selectedStepIds = [...initialSelection];
  state.onConfirm = onConfirm;
  state.onCancel = onCancel;
  state.onReopenPanel = onReopenPanel || null;
  state.isActive = true;
}

/**
 * Toggle selection of a step
 * @param stepId - The step ID to toggle
 */
export function toggleStepSelection(stepId: string) {
  const index = state.selectedStepIds.indexOf(stepId);
  if (index === -1) {
    state.selectedStepIds.push(stepId);
  } else {
    state.selectedStepIds.splice(index, 1);
  }
}

/**
 * Check if a step is currently selected
 * @param stepId - The step ID to check
 */
export function isStepSelected(stepId: string): boolean {
  return state.selectedStepIds.includes(stepId);
}

/**
 * Confirm the current selection and exit selection mode
 */
export function confirmSelection() {
  // Store the confirmed selection before resetting
  state.lastConfirmedSelection = [...state.selectedStepIds];
  
  if (state.onConfirm) {
    state.onConfirm([...state.selectedStepIds]);
  }
  const reopenCallback = state.onReopenPanel;
  resetSelectionState();
  if (reopenCallback) {
    reopenCallback();
  }
}

/**
 * Get and clear the last confirmed selection
 */
export function consumeLastConfirmedSelection(): string[] | null {
  const selection = state.lastConfirmedSelection;
  state.lastConfirmedSelection = null;
  return selection;
}

/**
 * Cancel selection and exit selection mode
 */
export function cancelSelection() {
  if (state.onCancel) {
    state.onCancel();
  }
  const reopenCallback = state.onReopenPanel;
  resetSelectionState();
  if (reopenCallback) {
    reopenCallback();
  }
}

/**
 * Reset the selection state
 */
function resetSelectionState() {
  state.isActive = false;
  state.selectableTypes = [];
  state.selectedStepIds = [];
  state.onConfirm = null;
  state.onCancel = null;
  state.onReopenPanel = null;
}

/**
 * Force reset selection mode (e.g., when changing scenarios)
 */
export function forceResetSelection() {
  state.lastConfirmedSelection = null;
  resetSelectionState();
}

/**
 * Composable hook for linked operator selection
 */
export function useLinkedOperatorSelection() {
  const isActive = computed(() => state.isActive);
  const selectableTypes = computed(() => state.selectableTypes);
  const selectableStepIds = computed(() => state.selectableStepIds);
  const selectedStepIds = computed(() => state.selectedStepIds);

  return {
    isActive,
    selectableTypes,
    selectableStepIds,
    selectedStepIds,
    startLinkedOperatorSelection,
    toggleStepSelection,
    isStepSelected,
    confirmSelection,
    cancelSelection,
  };
}
